import {faCircleCheck} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import DOMPurify from 'dompurify';
import {isNull} from 'lodash';
import React, {ReactNode} from 'react';
import {z as r} from 'zod';

import {Locale} from '../../../../../../provider/locale/locale';
import {SnackbarArgsType, SnackbarType} from '../../../../../../provider/snackbar/snackbar-type';
import {ReviewsReplyError} from '../../../../../../service/reviews/reviews-reply';
import {TagGroupType} from '../../../../../../service/reviews/reviews-tags';
import {HttpError} from '../../../../../../util/error';
import {getFromLocalStorage} from '../../../../../../util/local-storage';
import {isRecord} from '../../../../../../util/object';
import {URL_REGEXP} from '../../../../../../util/regexp';
import {toTrimmedString} from '../../../../../../util/string';
import {IS_SEND_ENTER_VARIANT_LOCALSTORAGE_KEY} from '../../../reviews-header/reviews-header-const';

import {ReviewLazyImage} from './lazy-image/review-lazy-image';
import {PreviewGroup} from './preview-group/preview-group';
import * as styles from './new-review.scss';

const CATALOG_ID_ASN_NEWS = 213;

export function convertCatalogCommentAttributes(text: string, catalogId?: number): string {
    if (catalogId !== CATALOG_ID_ASN_NEWS) {
        return text;
    }

    return text
        .replace(/problem_solved:\s*true/gi, 'Проблема решена')
        .replace(/moderation_message:\s*/gi, 'Администратор: ')
        .replace(/дополнительная информация:\s*/gi, '');
}

const PROBLEM_SOLVED_REGEXP = /problem_solved:\s*true/i;
const MODERATION_MESSAGE_LINE_REGEXP = /^moderation_message:\s*(.*)$/i;

type HasProblemSolvedInCommentParametersType = {
    comment: string | null;
    catalogId?: number;
};

export function hasProblemSolvedInComment({comment, catalogId}: HasProblemSolvedInCommentParametersType): boolean {
    return catalogId === CATALOG_ID_ASN_NEWS && Boolean(comment && PROBLEM_SOLVED_REGEXP.test(comment));
}

export function ReviewProblemSolvedBadge(): JSX.Element {
    return (
        <div className={styles.ReviewCatalogAttribute_problemSolved}>
            <FontAwesomeIcon icon={faCircleCheck} />
            <Locale stringKey="REVIEW__PROBLEM_SOLVED_LABEL" />
        </div>
    );
}

// we get the string with two <img> tags here, first with the base64 src and second with the http link to source image
function renderLazyImage(imageTags: string) {
    const [base64, http] = new DOMParser().parseFromString(imageTags, 'text/html').querySelectorAll('img');
    const image = http || base64;

    if (!image) {
        return null;
    }

    return <ReviewLazyImage alt={image.alt} src={image.src} />;
}

function constructCommentDocument(text: string): Document {
    const trimmedText = toTrimmedString(text);
    const cleanedText = DOMPurify.sanitize(trimmedText);

    return new DOMParser().parseFromString(cleanedText, 'text/html');
}

function getImagesFromCommentDocument(commentDocument: Document): NodeListOf<HTMLImageElement> {
    return commentDocument.querySelectorAll('img');
}

function getVideosFromCommentDocument(commentDocument: Document): NodeListOf<HTMLVideoElement> {
    return commentDocument.querySelectorAll('video');
}

function getVideoSrc(videoElement: HTMLVideoElement): string | null {
    const src = videoElement.getAttribute('src');

    if (src) {
        return src;
    }

    const sourceElement = videoElement.querySelector('source');

    return sourceElement?.getAttribute('src') || null;
}

function getMediaTagsFromCommentText(text: string): {
    images: Array<{src: string | null; alt: string | null}>;
    videos: Array<{src: string | null; poster: string | null}>;
} {
    const commentDocument = constructCommentDocument(text);

    const images = [...getImagesFromCommentDocument(commentDocument)].map((image) => ({
        src: image.getAttribute('src'),
        alt: image.getAttribute('alt'),
    }));

    const videos = [...getVideosFromCommentDocument(commentDocument)].map((video) => ({
        src: getVideoSrc(video),
        poster: video.getAttribute('poster'),
    }));

    return {images, videos};
}

const PROBLEM_SOLVED_LINE_REGEXP = /^problem_solved:\s*true$/i;

type ConstructCommentParametersType = {
    text: string;
    withLinks?: boolean;
    catalogId?: number;
};

const ADDITIONAL_INFO_PREFIX_REGEXP = /дополнительная информация:\s*/gi;

// eslint-disable-next-line sonarjs/cognitive-complexity
export function constructComment({text, withLinks, catalogId}: ConstructCommentParametersType): Array<JSX.Element> {
    const trimmedText = toTrimmedString(text).replace(ADDITIONAL_INFO_PREFIX_REGEXP, '');

    const {images, videos} = getMediaTagsFromCommentText(trimmedText);

    const mediaGallery = <PreviewGroup imageClassName={styles.ReviewGallery_image} images={images} videos={videos} />;

    if (trimmedText === '') {
        return [];
    }

    const isCatalogAsnNews = catalogId === CATALOG_ID_ASN_NEWS;

    const trimmedTextArray = trimmedText
        .split('\n')
        .filter((filteredText) => {
            const trimmedLine = filteredText.trim();

            if (trimmedLine.startsWith('<img') || trimmedLine.startsWith('<video')) {
                return false;
            }

            if (/<img[\s>]|<video[\s>]/i.test(trimmedLine)) {
                const lineDocument = constructCommentDocument(trimmedLine);
                const lineText = (lineDocument.body.textContent || '').trim();

                if (lineText === '') {
                    return false;
                }
            }

            return (
                !images.some((image) => (image.src ? filteredText.includes(image.src) : false)) &&
                !videos.some((video) => (video.src ? filteredText.includes(video.src) : false))
            );
        })
        .map((part: string, index: number): JSX.Element | null => {
            const trimmedPart = part.trim();

            if (isCatalogAsnNews && PROBLEM_SOLVED_LINE_REGEXP.test(trimmedPart)) {
                return null;
            }

            if (isCatalogAsnNews && trimmedPart && MODERATION_MESSAGE_LINE_REGEXP.test(trimmedPart)) {
                const match = trimmedPart.match(MODERATION_MESSAGE_LINE_REGEXP);
                const message = match?.[1]?.trim() ?? '';

                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <div className={styles.ReviewCatalogAttribute_administrator} key={`moderation-${index}`}>
                        <strong>
                            <Locale stringKey="REVIEW__ADMINISTRATOR_LABEL" />:
                        </strong>{' '}
                        {message}
                    </div>
                );
            }

            let cleanedText = part === '' ? '<br/>' : DOMPurify.sanitize(part);
            const hasImage = cleanedText.includes('<img');

            if (withLinks) {
                cleanedText = cleanedText
                    .split(' ')
                    .map((substring) => substring.replace(URL_REGEXP, (url) => `<a href="${url}">${url}</a>`))
                    .join(' ');
            }

            cleanedText = cleanedText.replaceAll('<a', '<a target="_blank"');

            if (hasImage) {
                // eslint-disable-next-line react/no-array-index-key
                return <div key={String(index)}>{renderLazyImage(cleanedText)}</div>;
            }

            return (
                // eslint-disable-next-line react/no-danger, react/no-array-index-key
                <div dangerouslySetInnerHTML={{__html: cleanedText.trim()}} key={String(index)} />
            );
        })
        .filter((element): element is JSX.Element => element !== null);

    return [...trimmedTextArray, mediaGallery];
}

export function extractReviewCommentContent(comment: string | null): {
    images: Array<HTMLImageElement>;
    videos: Array<{src: string | null; poster: string | null}>;
    textWithImages: Array<React.JSX.Element>;
} | null {
    if (!comment) {
        return null;
    }

    const commentDocument = constructCommentDocument(comment);

    const textWithImages = constructComment({text: comment});
    const images = [...getImagesFromCommentDocument(commentDocument)];
    const videos = [...getVideosFromCommentDocument(commentDocument)].map((video) => ({
        src: getVideoSrc(video),
        poster: video.getAttribute('poster'),
    }));

    return {images, videos, textWithImages};
}

export function constructCommentString(text: string): string {
    const commentDocument = constructCommentDocument(text);
    const initialText: string = commentDocument.documentElement.textContent || '';

    return initialText.replace(/(\n){3,}/g, '\n\n'); // two \n (new lines) in arrow is maximum
}

export function constructCommentPreview(text: string, limit = 2500): string {
    const commentString: string = constructCommentString(text);

    if (commentString.length > limit) {
        return `${commentString.slice(0, limit)}…`;
    }

    return commentString;
}

/**
 * `visiblePlainTarget` must match non-search rendering (`constructCommentPreview` + same collapse/expand slice).
 * Inserts &lt;mark&gt; from the search payload. `FORBID_TAGS: ['img', 'video']` — media only via PreviewGroup.
 */
export function buildReviewCommentHtmlWithSearchMarks(displayComment: string, visiblePlainTarget: string): string {
    const sanitizedMarked = DOMPurify.sanitize(displayComment, {FORBID_TAGS: ['img', 'video']});

    if (toTrimmedString(sanitizedMarked) === '') {
        return '';
    }

    const endsWithEllipsis = visiblePlainTarget.endsWith('…');
    const plainForMerge = endsWithEllipsis ? visiblePlainTarget.slice(0, -1) : visiblePlainTarget;

    const markedDocument = new DOMParser().parseFromString(sanitizedMarked, 'text/html');

    let assembled = '';
    let cursor = 0;

    [...markedDocument.querySelectorAll('mark')].forEach((mark) => {
        const segment = mark.textContent ?? '';

        if (segment === '') {
            return;
        }

        const index = plainForMerge.indexOf(segment, cursor);

        if (index === -1) {
            return;
        }

        assembled += DOMPurify.sanitize(plainForMerge.slice(cursor, index));
        assembled += `<mark>${DOMPurify.sanitize(segment)}</mark>`;
        cursor = index + segment.length;
    });

    assembled += DOMPurify.sanitize(plainForMerge.slice(cursor));

    let html = DOMPurify.sanitize(assembled, {FORBID_TAGS: ['img', 'video']});

    if (endsWithEllipsis) {
        html += '…';
    }

    return html;
}

export function handleReviewReplyExceptions(
    error: unknown,
    snackbar: SnackbarType,
    snackbarArgs?: SnackbarArgsType,
    handle423Error?: () => void
): void {
    if (error instanceof ReviewsReplyError) {
        if (error.statusCode === 423 && handle423Error) {
            handle423Error();
        }

        // eslint-disable-next-line react/jsx-no-useless-fragment
        snackbar.error(<>{error.jsonData.error || error.jsonData.detail}</>);
        return;
    }

    snackbar.error(
        snackbarArgs ?? {
            description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
            message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
        }
    );

    if (!(error instanceof HttpError)) {
        // This is not HttpError|ApiError. This error is in js code. A developer should see it in a console
        console.error(error);
    }
}

export function newHandleEnter(
    event: React.KeyboardEvent<HTMLTextAreaElement>,
    submitHandler: (() => Promise<void>) | null
): void {
    const localStorageVariantSendReply = getFromLocalStorage(
        IS_SEND_ENTER_VARIANT_LOCALSTORAGE_KEY,
        r.boolean(),
        false
    );

    if (localStorageVariantSendReply) {
        if (!event.shiftKey) {
            event.preventDefault();
            submitHandler?.();
        }
    } else if (event.shiftKey) {
        event.preventDefault();
        submitHandler?.();
    }
}

export function checkHasJsonData(error: unknown): error is {jsonData: ReactNode} {
    return isRecord(error) && Reflect.has(error, 'jsonData');
}

const onlyEmojiPattern = /^(?:\s|\p{Extended_Pictographic}|\p{Emoji_Modifier}|\uFE0F|\u200D)+$/u;

export function isOnlyEmojis(text: string | null): boolean {
    if (isNull(text)) {
        return false;
    }

    const trimmed = toTrimmedString(text);

    if (trimmed === '') {
        return false;
    }

    return onlyEmojiPattern.test(trimmed);
}

export function getMissingRequiredGroups(
    groups: Array<TagGroupType>,
    reviewGroupIds: Array<number>
): Array<TagGroupType> {
    const requiredGroups = groups.filter((group) => group.isRequired);

    const reviewIdSet = new Set(reviewGroupIds);

    const hasIntersection = requiredGroups.some((group) => reviewIdSet.has(group.id));

    if (!hasIntersection) {
        return requiredGroups;
    }

    return requiredGroups.filter((group) => !reviewIdSet.has(group.id));
}

async function processBatchRecursive<T>(
    items: Array<T>,
    batchSize: number,
    processor: (item: T) => Promise<unknown>,
    startIndex: number
): Promise<void> {
    if (startIndex >= items.length) {
        return;
    }

    const batch = items.slice(startIndex, startIndex + batchSize);

    await Promise.allSettled(
        batch.map((item) =>
            processor(item).catch((error) => {
                console.error('Error processing item:', error);
            })
        )
    );

    // Small delay between batches to avoid overwhelming the server
    if (startIndex + batchSize < items.length) {
        await new Promise((resolve) => {
            setTimeout(resolve, 50);
        });

        await processBatchRecursive(items, batchSize, processor, startIndex + batchSize);
    }
}

export async function processBatch<T>(
    items: Array<T>,
    batchSize: number,
    processor: (item: T) => Promise<unknown>
): Promise<void> {
    await processBatchRecursive(items, batchSize, processor, 0);
}
